import { takeLatest, takeEvery, call, put, spawn } from 'redux-saga/effects';
import { AuthClientUtils } from 'ducks/auth-client';

import {
  CloudActions,
  LicenseServiceUrls,
  CloudErrors,
  AuthUtils,
  FlowjoCommons,
} from '@portal/portal-core';

import * as Actions from '../actions';
import * as Types from '../types';

const { Alerts, Net } = FlowjoCommons;

// Shape of the saga config object threaded through the watcher sagas.
interface AgreementsSagaConfig {
  baseApiUrl: string;
  getAuthToken: (key?: string) => string;
}

// Shape of the dispatched action fields accessed by these sagas.
interface AgreementAction {
  type: string;
  userId?: unknown;
  agreementId?: unknown;
  productName?: string;
  productAccessType?: string;
  productVersion?: string;
  ccCountry?: string;
  region?: string;
  agreementType?: string;
}

// Net.sendJson responses whose `data` is a raw JSON string from the server.
interface AgreementTextResponse {
  data?: string;
  code: number;
}

// Net.sendJson responses whose `data` is an already-parsed object.
interface AcceptanceResponse {
  data?: { succeeded?: boolean; result?: unknown };
  code: number;
}

// Parsed agreement payload (from JSON.parse of the response text).
interface ParsedAgreement {
  result?: { id?: unknown; text?: unknown };
  error?: string;
  succeeded?: boolean;
}

export function* dismissSignInConnectionProblem(): Generator<unknown, void, unknown> {
  const errorType = CloudErrors.cloudErrorTypes.CONNECTION_PROBLEM;
  yield put(Alerts.AlertActions.dismissAlert(errorType));
}

export function* dismissmissingUserAgreement(): Generator<unknown, void, unknown> {
  const errorType = CloudErrors.cloudErrorTypes.CANNOT_FIND_USER_AGREEMENT;
  yield put(Alerts.AlertActions.dismissAlert(errorType));
}

export function* getUserAgreementAcceptance(
  config: AgreementsSagaConfig,
  action: AgreementAction,
): Generator<unknown, unknown, unknown> {
  try {
    const authToken = config.getAuthToken();
    const headers = { Authorization: `Bearer ${authToken}` };
    const url = LicenseServiceUrls.userAgreementAcceptedUrl(config.baseApiUrl);
    // consider refactoring to translate the action provided to the server action
    const userAgreementAcceptedGetAction = CloudActions.userAgreementAcceptedGet(
      action.userId,
      action.agreementId,
    );
    const { data, code } = (yield call(
      Net.sendJson,
      url,
      userAgreementAcceptedGetAction,
      headers,
    )) as AcceptanceResponse;
    if (code !== Net.reponses.OK || data!.succeeded === false) {
      // could not get through consider error handling here.
      return null;
    }
    return data!.result;
  } catch (error) {
    console.log('Could not get user agreement acceptance ', error);
    return null;
  }
}

export function* setUserAgreementAcceptance(
  config: AgreementsSagaConfig,
  action: AgreementAction,
): Generator<unknown, unknown, unknown> {
  try {
    const authToken = config.getAuthToken(AuthClientUtils.getAuthTokenKey());
    const headers = { Authorization: `Bearer ${authToken}` };
    const url = LicenseServiceUrls.userAgreementAcceptedUrl(config.baseApiUrl);
    const serverAction = CloudActions.userAgreementAcceptedSet(action.userId, action.agreementId);
    const { data, code } = (yield call(Net.sendJson, url, serverAction, headers)) as AcceptanceResponse;

    if (code !== Net.reponses.OK || data!.succeeded === false) {
      // could not get through consider error handling here.
      return null;
    }
    yield spawn(dismissSignInConnectionProblem);
    // const result = data.result;
    yield put(Actions.userHasAgreed());
    return data!.result;
  } catch (error) {
    return null;
  }
}

export const sendUserAgreementGet = async (
  url: string,
  action: object,
  headers: Record<string, string> | null,
): Promise<unknown> => Net.sendJson(url, action, headers as unknown as {});
export const sendUserAgreementAcceptedGet = async (
  url: string,
  action: object,
  headers: Record<string, string> | null,
): Promise<unknown> => Net.sendJson(url, action, headers as unknown as {});

// this is the meat of our saga - the generator allows it to pause on the API call
// and then continue once it has resolved
export function* getUserAgreement(
  config: AgreementsSagaConfig,
  action: AgreementAction,
): Generator<unknown, void, unknown> {
  try {
    const url = LicenseServiceUrls.userAgreementUrl(config.baseApiUrl);
    const headers = null;
    const serverAction = CloudActions.userAgreementGet(
      action.productName,
      action.productAccessType,
      action.productVersion,
      action.ccCountry,
      action.region,
      action.agreementType,
    );
    // here we call the API - notice the lack of .then calls
    // the generator pauses until the result of yield comes back
    const { data, code } = (yield call(
      sendUserAgreementGet,
      url,
      serverAction,
      headers,
    )) as AgreementTextResponse;

    let dataObj: ParsedAgreement = {};
    if (data) {
      dataObj = JSON.parse(data);
    }

    // proceed as we did in middlwares
    if (code !== Net.reponses.OK) {
      const errorType = CloudErrors.cloudErrorTypes.CANNOT_FIND_USER_AGREEMENT;
      yield put(Alerts.AlertActions.dismissAlert(errorType));

      if (
        dataObj?.error === CloudErrors.userAgreementsErrorTypes.MISSING_USER_AGREEMENT &&
        action.productName
      ) {
        const alert = Alerts.Alert.AlertStamp({
          type: errorType,
        });
        yield put(Alerts.AlertActions.setAlert(alert));
      }
    } else {
      yield spawn(dismissSignInConnectionProblem);
      yield spawn(dismissmissingUserAgreement);

      const agreementId = dataObj.result!.id;
      // generate the auth token and attach it to the headers
      const authToken = config.getAuthToken(AuthClientUtils.getAuthTokenKey()) || '';
      if (authToken === '') {
        throw new Error('Could not get Auth Token. Please login');
      }
      const headers1 = { Authorization: `Bearer ${authToken}` };
      const userId = AuthUtils.getUserIdFromJWT(authToken);
      const urlAgreementAccepted = LicenseServiceUrls.userAgreementAcceptedUrl(config.baseApiUrl);
      const userAgreementAcceptedGetAction = CloudActions.userAgreementAcceptedGet(
        userId,
        agreementId,
      );
      // get the state of the user and the agreement (has the agreement been accepted by this user)
      try {
        const AgreementResult = (yield call(
          sendUserAgreementAcceptedGet,
          urlAgreementAccepted,
          userAgreementAcceptedGetAction,
          headers1,
        )) as AgreementTextResponse;

        const dataAgreement = AgreementResult.data;
        const codeAgreement = AgreementResult.code;

        let dataAgreementObj: ParsedAgreement = {};
        if (dataAgreement) {
          dataAgreementObj = JSON.parse(dataAgreement);
        }

        if (codeAgreement !== Net.reponses.OK) {
          throw new Error('Unable to find Agreement');
        } else if (!dataAgreementObj.result) {
          // legacy might not be needed requires more investication
          yield spawn(dismissSignInConnectionProblem);
          yield spawn(dismissmissingUserAgreement);

          // put userAgreement in the store
          const userAgreement = dataObj.result!.text;
          // const userAgreementId = dataAgreementObj.result.id;
          const userAgreementObj = { agreementId, userAgreement };

          yield put(Actions.userAgreementStowe(userAgreementObj));
          // agreementId originates from JSON.parse of the server response; it is the agreement's string id.
          yield put(Actions.userHasNotAgreedYet(agreementId as string));
          // The user has signed the agreement we are all good
        } else if (dataAgreementObj.succeeded === true && dataAgreementObj.result) {
          yield spawn(dismissSignInConnectionProblem);
          yield spawn(dismissmissingUserAgreement);
          yield put(Actions.userHasAgreed());
        }
      } catch (error) {
        // consider handling errors here
        console.log('there was a problem getting user agreement acceptance status', error);
      }
    }
  } catch (error) {
    // consider handling errors here as well
    console.log('there was a UserAgreementError', error);
  }
}

// our watcher saga - this saga simply listens for when 'INVITE_USER_PRODUCT_ACCESS_CANCEL_INVITES'
// is dispatched
// and then hands the action off to revokeInvite above
/* istanbul ignore next reason: no saga watch tests */
export function* watchClientUserAgreementGet(
  config: AgreementsSagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(Types.REQ_USER_AGREEMENT_GET, getUserAgreement, config);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSetUserAgreementAcceptance(
  config: AgreementsSagaConfig,
): Generator<unknown, void, unknown> {
  yield takeEvery(Types.REQ_USER_AGREEMENT_ACCEPTED_SET, setUserAgreementAcceptance, config);
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchGetUserAgreementAcceptance(
  config: AgreementsSagaConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(Types.REQ_USER_AGREEMENT_ACCEPTED_GET, getUserAgreementAcceptance, config);
}
